/**
 * @file User / material analysis UI — pages/Dashboard/User/MaterialAnalysisInput/utils/materialAnalysisExport.ts.
 */

import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

import type { FormValues } from "../hooks/useMaterialAnalysisForm";
import {
  formatMaterialAnalysisSubmittedOn,
  type MaterialAnalysisSubmission,
} from "../../../../../services/materialAnalysisService";
import { formatMaterialAnalysisGeographicalLabel } from "../../../../../utils/backgroundGeographicalSpecificity";
import type { DropdownOption } from "../../../../../interfaces/dropdownInterfaces";
import {
  formatReliabilityDropdownLabel,
  formatSubmissionDataReviewLabel,
} from "./reliabilityFieldLabels";

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

const CAPTURE_CLASS = "pdf-report-root--capturing";

const withPdfRootCapture = async <T>(
  pdfRoot: HTMLElement,
  fn: () => Promise<T>
): Promise<T> => {
  pdfRoot.classList.add(CAPTURE_CLASS);
  await wait(0);
  try {
    return await fn();
  } finally {
    pdfRoot.classList.remove(CAPTURE_CLASS);
  }
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const pad2 = (n: number) => String(n).padStart(2, "0");

const buildStamp = (date: Date) => {
  return `${pad2(date.getDate())}${date.toLocaleString("en-US", { month: "short" })}${date.getFullYear()}`;
};

const safeFilePart = (value: string) =>
  (value || "material").replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "");

/** Exported function — download Material Analysis Pdf. */
export async function downloadMaterialAnalysisPdf(args: {
  pdfRoot: HTMLElement;
  submissions: MaterialAnalysisSubmission[];
  selectedMaterialName?: string;
  /** Used by callers for parity with Excel; title comes from the hidden PDF report DOM. */
  tableTitle?: string;
  renderDelayMs?: number;
}) {
  const { pdfRoot, submissions, selectedMaterialName, renderDelayMs = 50 } = args;
  if (!submissions?.length) return;

  await wait(renderDelayMs);

  await withPdfRootCapture(pdfRoot, async () => {
    const pages = Array.from(pdfRoot.querySelectorAll<HTMLElement>("[data-pdf-page]"));
    if (pages.length === 0) return;

    const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const pdfW = pdf.internal.pageSize.getWidth();
    const pdfH = pdf.internal.pageSize.getHeight();

    for (let i = 0; i < pages.length; i += 1) {
      const el = pages[i];
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const imgData = canvas.toDataURL("image/png");
      const imgW = canvas.width;
      const imgH = canvas.height;
      const ratio = Math.min(pdfW / imgW, pdfH / imgH);
      const renderW = imgW * ratio;
      const renderH = imgH * ratio;
      const x = (pdfW - renderW) / 2;
      const y = (pdfH - renderH) / 2;
      if (i > 0) pdf.addPage();
      pdf.addImage(imgData, "PNG", x, y, renderW, renderH);
    }

    const date = new Date();
    const stamp = buildStamp(date);
    const safeMaterial = safeFilePart(selectedMaterialName || "material");
    pdf.save(`ICoMEC_Dashboard_Result_${safeMaterial}_${stamp}.pdf`);
  });
}

/** Exported function — download Material Analysis Excel. */
export async function downloadMaterialAnalysisExcel(args: {
  pdfRoot: HTMLElement;
  submissions: MaterialAnalysisSubmission[];
  formValues: FormValues;
  geoLabel: string;
  selectedMaterialName: string;
  selectedSubMaterialName: string;
  materialPropertyLabel: string;
  selectedTechnologyName: string;
  selectedManufacturerName: string;
  tableTitle?: string;
  dataReviewOptions?: DropdownOption[];
  dataCompletenessOptions?: DropdownOption[];
  dataAcquisitionOptions?: DropdownOption[];
  renderDelayMs?: number;
}) {
  const {
    pdfRoot,
    submissions,
    formValues,
    geoLabel,
    selectedMaterialName,
    selectedSubMaterialName,
    materialPropertyLabel,
    selectedTechnologyName,
    selectedManufacturerName,
    tableTitle = "Datasets meeting input requirements",
    dataReviewOptions = [],
    dataCompletenessOptions = [],
    dataAcquisitionOptions = [],
    renderDelayMs = 50,
  } = args;

  if (!submissions?.length) return;

  const { Workbook } = await import("exceljs");

  const getGeographicalLabel = (s: MaterialAnalysisSubmission): string =>
    formatMaterialAnalysisGeographicalLabel(s);

  const pickCarbonNumber = (s: MaterialAnalysisSubmission): number | null => {
    const summary = s.a1a3Summary || s.a3Summary || s.a2Summary || s.a1Summary || null;
    const raw = summary?.embodiedCarbonValue;
    const n = typeof raw === "number" ? raw : Number(raw);
    if (!Number.isFinite(n)) return null;
    return n;
  };

  await wait(renderDelayMs);

  let graphBase64: string | null = null;
  await withPdfRootCapture(pdfRoot, async () => {
    const graphEl = pdfRoot.querySelector<HTMLElement>('[data-pdf-page="1"] .pdf-grid__right');
    if (!graphEl) return;
    try {
      const graphCanvas = await html2canvas(graphEl, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      graphBase64 = graphCanvas.toDataURL("image/png").split(",")[1] ?? null;
    } catch (error) {
      console.error("Failed to capture graph for Excel export:", error);
    }
  });

  const workbook = new Workbook();
  workbook.creator = "ICoMEC Dashboard";
  workbook.created = new Date();

  const sheet = workbook.addWorksheet("ICoMEC Result", {
    pageSetup: {
      orientation: "landscape",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 1,
    },
    properties: { defaultRowHeight: 18 },
    views: [{ state: "frozen", ySplit: 1 }],
  });

  // Column widths (roughly matching PDF layout)
  sheet.columns = [
    { key: "a", width: 24 },
    { key: "b", width: 42 },
    { key: "c", width: 2 },
    { key: "d", width: 20 },
    { key: "e", width: 20 },
    { key: "f", width: 20 },
    { key: "g", width: 20 },
    { key: "h", width: 20 },
    { key: "i", width: 14 },
    { key: "j", width: 14 },
    { key: "k", width: 14 },
    { key: "l", width: 16 },
  ];

  // Banner
  sheet.mergeCells("A1:L1");
  const bannerCell = sheet.getCell("A1");
  bannerCell.value = "ICoMEC Dashboard Result";
  bannerCell.font = { bold: true, size: 16, color: { argb: "FFFFFFFF" } };
  bannerCell.alignment = { vertical: "middle", horizontal: "center" };
  bannerCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FF2B4DBA" },
  };
  sheet.getRow(1).height = 28;

  // Input conditions (same order as PDF)
  const kvStartRow = 3;
  const kv = [
    ["Material", selectedMaterialName || "-"],
    ["Material Sub-type", selectedSubMaterialName || "-"],
    ["Material Properties", materialPropertyLabel || "-"],
    ["Data Review", formValues.dataReview || "-"],
    ["Data Completeness", formValues.dataCompleteness || "-"],
    ["Data Acquisition", formValues.dataAcquisition || "-"],
    ["Temporal Specificity", `${formValues.temporalRange?.[0]} – ${formValues.temporalRange?.[1]}`],
    ["Geographical Specificity", geoLabel || "-"],
    ["Technological Specificity", selectedTechnologyName || "Agnostic"],
    ["Manufacturer Specificity", selectedManufacturerName || "Agnostic"],
  ] as const;

  sheet.getCell(`A${kvStartRow - 1}`).value = "Input conditions";
  sheet.getCell(`A${kvStartRow - 1}`).font = { bold: true, size: 12 };

  kv.forEach(([k, v], idx) => {
    const r = kvStartRow + idx;
    const keyCell = sheet.getCell(`A${r}`);
    const valCell = sheet.getCell(`B${r}`);
    keyCell.value = k;
    keyCell.font = { bold: true };
    keyCell.alignment = { vertical: "top", wrapText: true };
    valCell.value = v;
    valCell.alignment = { vertical: "top", wrapText: true };
  });

  // Graph image (captured from hidden PDF report)
  if (graphBase64) {
    const imageId = workbook.addImage({
      base64: graphBase64,
      extension: "png",
    });
    // Place to the right of input conditions
    sheet.addImage(imageId, {
      tl: { col: 3, row: 2 }, // D3 (0-indexed)
      ext: { width: 720, height: 300 },
    });
  } else {
    sheet.getCell("D3").value = "Graph unavailable";
    sheet.getCell("D3").font = { italic: true, color: { argb: "FF666666" } };
  }

  // Table
  const tableTitleRow = 22;
  sheet.getCell(`A${tableTitleRow}`).value = tableTitle;
  sheet.getCell(`A${tableTitleRow}`).font = { bold: true, size: 12 };

  const headerRow = tableTitleRow + 1;
  const headers = [
    "Dataset ID",
    "Submitted on",
    "Material",
    "Material\nSub-type",
    "Data\nReview",
    "Data\nCompleteness",
    "Data\nAcquisition",
    "Temporal\nRepresentativeness",
    "Geographical\nRepresentativeness",
    "Technological\nRepresentativeness",
    "Manufacturer\nRepresentativeness",
    "Cradle to Gate Embodied Carbon Value\n[kg CO₂eq/kg]",
  ];
  const headerRowRef = sheet.getRow(headerRow);
  headers.forEach((header, idx) => {
    headerRowRef.getCell(idx + 1).value = header;
  });
  headerRowRef.font = { bold: true, color: { argb: "FFFFFFFF" } };
  headerRowRef.alignment = {
    vertical: "middle" as const,
    horizontal: "center" as const,
  };
  headerRowRef.height = 42;
  for (let c = 1; c <= headers.length; c += 1) {
    const cell = sheet.getRow(headerRow).getCell(c);
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F2937" } };
    cell.alignment = {
      vertical: "middle" as const,
      horizontal: "center" as const,
      wrapText: true,
    };
    cell.border = {
      top: { style: "thin", color: { argb: "FFCBD5E1" } },
      left: { style: "thin", color: { argb: "FFCBD5E1" } },
      bottom: { style: "thin", color: { argb: "FFCBD5E1" } },
      right: { style: "thin", color: { argb: "FFCBD5E1" } },
    };
  }

  const tableRows = (submissions || []).map((s) => {
    const ec = pickCarbonNumber(s);
    return [
      s.submissionCode || s.submissionId,
      formatMaterialAnalysisSubmittedOn(s),
      s.materialName,
      s.subMaterialName || "-",
      formatSubmissionDataReviewLabel(s.dataReview, dataReviewOptions),
      formatReliabilityDropdownLabel(dataCompletenessOptions, s.dataCompleteness),
      formatReliabilityDropdownLabel(dataAcquisitionOptions, s.dataAcquisition),
      `${s.startYear ?? "-"} – ${s.endYear ?? "-"}`,
      getGeographicalLabel(s),
      s.technologicalSpecificity?.name ?? "-",
      s.manufacturerSpecificity?.name ?? "-",
      ec == null ? "-" : Number(ec.toFixed(2)),
    ];
  });

  const firstDataRow = headerRow + 1;
  tableRows.forEach((r, i) => {
    const rowIdx = firstDataRow + i;
    const rowRef = sheet.getRow(rowIdx);
    r.forEach((value, idx) => {
      rowRef.getCell(idx + 1).value = value;
    });
    rowRef.alignment = { vertical: "top", wrapText: true };
    for (let c = 1; c <= headers.length; c += 1) {
      const cell = sheet.getRow(rowIdx).getCell(c);
      cell.border = {
        top: { style: "thin", color: { argb: "FFCBD5E1" } },
        left: { style: "thin", color: { argb: "FFCBD5E1" } },
        bottom: { style: "thin", color: { argb: "FFCBD5E1" } },
        right: { style: "thin", color: { argb: "FFCBD5E1" } },
      };
    }
  });

  // Improve table column widths
  sheet.getColumn(1).width = 16;
  sheet.getColumn(2).width = 16;
  sheet.getColumn(3).width = 22;
  sheet.getColumn(4).width = 18;
  sheet.getColumn(5).width = 16;
  sheet.getColumn(6).width = 16;
  sheet.getColumn(7).width = 16;
  sheet.getColumn(8).width = 14;
  sheet.getColumn(9).width = 14;
  sheet.getColumn(10).width = 14;
  sheet.getColumn(11).width = 14;
  sheet.getColumn(12).width = 22;

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const date = new Date();
  const stamp = buildStamp(date);
  const safeMaterial = safeFilePart(selectedMaterialName || "material");
  triggerBlobDownload(blob, `ICoMEC_Dashboard_Result_${safeMaterial}_${stamp}.xlsx`);
}

